import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiMail, FiPhone, FiMapPin, FiTwitter, FiInstagram, FiLinkedin, FiYoutube, FiHeart, FiArrowUp } = FiIcons;

const Footer = () => {
  const services = [
    { name: 'OTT Channel Development', href: '#services' },
    { name: 'YouTube Management', href: '#services' },
    { name: 'Social Media Strategy', href: '#services' },
    { name: 'Community Platforms', href: '#services' }
  ];

  const resources = [
    { name: 'LGBTQ+ Creator Resources', href: '#' },
    { name: 'NYC Media Landscape', href: '#' },
    { name: 'Platform Best Practices', href: '#' },
    { name: 'Success Stories', href: '#creators' }
  ];

  const socialLinks = [
    { icon: FiTwitter, href: '#', label: 'Twitter' },
    { icon: FiInstagram, href: '#', label: 'Instagram' },
    { icon: FiLinkedin, href: '#', label: 'LinkedIn' },
    { icon: FiYoutube, href: '#', label: 'YouTube' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer id="contact" className="bg-gray-950 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-4 gap-16">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <motion.div
              className="flex flex-col sm:flex-row items-center sm:items-start space-y-6 sm:space-y-0 sm:space-x-8 mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://s3.us-east-1.wasabisys.com/createspace/CSM-Logo.png" 
                alt="Create Space Media Logo" 
                className="w-40 h-40 lg:w-56 lg:h-56 object-contain"
              />
              <div className="text-center sm:text-left pt-4">
                
                
              </div>
            </motion.div>
            
            <motion.p
              className="text-gray-300 text-xl leading-relaxed mb-12 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Empowering LGBTQ+ content creators and niche communities to build sustainable, community-driven digital ecosystems. Based in New York City, serving creators worldwide.
            </motion.p>

            {/* Contact Info */}
            <motion.div
              className="grid sm:grid-cols-2 gap-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-5 text-gray-300 text-lg group">
                <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center border border-gray-800 group-hover:border-purple-500/50 transition-colors">
                  <SafeIcon icon={FiMapPin} className="text-purple-400 text-xl" />
                </div>
                <span className="font-medium">New York City, NY</span>
              </div>
              <div className="flex items-center space-x-5 text-gray-300 text-lg group">
                <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center border border-gray-800 group-hover:border-purple-500/50 transition-colors">
                  <SafeIcon icon={FiMail} className="text-purple-400 text-xl" />
                </div>
                <span className="font-medium">hello@createspacemedia.com</span>
              </div>
            </motion.div>
          </div>

          {/* Services Links */}
          <div>
            <motion.h4
              className="text-2xl font-bold text-white mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Services
            </motion.h4>
            <motion.ul
              className="space-y-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {services.map((service) => (
                <li key={service.name}>
                  <motion.a
                    href={service.href}
                    className="text-gray-400 hover:text-purple-400 text-xl transition-colors duration-200 flex items-center space-x-2"
                    whileHover={{ x: 8 }}
                  >
                    <span className="w-1.5 h-1.5 bg-purple-500/50 rounded-full"></span>
                    <span>{service.name}</span>
                  </motion.a>
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Resources Links */}
          <div>
            <motion.h4
              className="text-2xl font-bold text-white mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              Resources
            </motion.h4>
            <motion.ul
              className="space-y-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              {resources.map((resource) => (
                <li key={resource.name}>
                  <motion.a
                    href={resource.href}
                    className="text-gray-400 hover:text-purple-400 text-xl transition-colors duration-200 flex items-center space-x-2"
                    whileHover={{ x: 8 }}
                  >
                    <span className="w-1.5 h-1.5 bg-pink-500/50 rounded-full"></span>
                    <span>{resource.name}</span>
                  </motion.a>
                </li>
              ))}
            </motion.ul>
          </div>
        </div>

        {/* Social Links */}
        <motion.div
          className="flex items-center justify-center space-x-8 mt-24 pt-12 border-t border-gray-800"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          viewport={{ once: true }}
        >
          {socialLinks.map((social) => (
            <motion.a
              key={social.label}
              href={social.href}
              className="w-16 h-16 bg-gray-900 border border-gray-800 hover:border-purple-500/50 hover:bg-gray-800/50 rounded-2xl flex items-center justify-center transition-all duration-300 group"
              whileHover={{ scale: 1.1, y: -8 }}
              whileTap={{ scale: 0.9 }}
              aria-label={social.label}
            >
              <SafeIcon icon={social.icon} className="text-gray-400 group-hover:text-purple-400 text-2xl" />
            </motion.a>
          ))}
        </motion.div>

        {/* Bottom Section */}
        <div className="mt-20 pt-10 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0">
          <motion.div
            className="text-center md:text-left"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 text-lg mb-2 font-medium">
              © 2024 Create Space Media. All rights reserved.
            </p>
            <p className="text-gray-500 text-sm flex items-center justify-center md:justify-start space-x-2">
              <span>Made with</span>
              <SafeIcon icon={FiHeart} className="text-pink-500 animate-pulse" />
              <span>for the LGBTQ+ community in NYC</span>
            </p>
          </motion.div>

          <motion.button
            onClick={scrollToTop}
            className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-2xl flex items-center justify-center shadow-2xl hover:shadow-purple-500/40 transition-all duration-300 group"
            whileHover={{ scale: 1.1, y: -5 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            viewport={{ once: true }}
          >
            <SafeIcon icon={FiArrowUp} className="text-white text-2xl group-hover:animate-bounce" />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;